import { message as antdMessage } from 'antd';
import type { ArgsProps, ConfigOptions, MessageApi } from 'antd/es/message';
import type { ReactNode } from 'react';
import { getStore } from 'src/store/storeRef';
import { getTheme } from 'src/theming/connectTheme';

const MESSAGE_WRAPPER_CLASS = 'ds-message';

let styleElement: HTMLStyleElement | null = null;
let currentTheme: string | null = null;

/**
 * Styles for message API methods (message.info, message.success, message.error, etc.).
 *
 * These methods render messages outside the React component tree, so JSS styles from useStyles()
 * are not applied. We inject these styles directly into the document head and update them
 * when the theme changes to ensure proper theming support.
 */
const generateStyles = (theme: any) => `
  .${MESSAGE_WRAPPER_CLASS} .ant-message-notice-content,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-notice-content {
    background-color: ${theme.color.semantic.background.neutral.secondary} !important;
    color: ${theme.color.semantic.text.primary} !important;
    padding: ${theme.spacing[3]} !important;
    border-radius: ${theme.radius.sm} !important;
    box-shadow: ${theme.shadow.sm} !important;
  }

  .${MESSAGE_WRAPPER_CLASS} .ant-message-error .anticon,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-error .anticon {
    color: ${theme.color.semantic.brand.danger.primary} !important;
  }

  .${MESSAGE_WRAPPER_CLASS} .ant-message-success .anticon,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-success .anticon {
    color: ${theme.color.semantic.accent.green.strong} !important;
  }

  .${MESSAGE_WRAPPER_CLASS} .ant-message-warning .anticon,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-warning .anticon {
    color: ${theme.color.semantic.accent.orange.strong} !important;
  }

  .${MESSAGE_WRAPPER_CLASS} .ant-message-info .anticon,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-info .anticon,
  .${MESSAGE_WRAPPER_CLASS} .ant-message-loading .anticon,
  .ant-message-notice.${MESSAGE_WRAPPER_CLASS} .ant-message-loading .anticon {
    color: ${theme.color.semantic.brand.default.primary} !important;
  }
`;

const injectStyles = () => {
  // Guard for SSR environments
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const store = getStore();
    const state = store?.getState();
    const newTheme = state?.uiTheme?.currentTheme || 'light';

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.setAttribute('data-ds-message-styles', 'true');
      if (document.head) {
        document.head.appendChild(styleElement);
      }
      currentTheme = newTheme;
      styleElement.textContent = generateStyles(getTheme(state));
    } else if (currentTheme !== newTheme) {
      currentTheme = newTheme;
      styleElement.textContent = generateStyles(getTheme(state));
    }
  } catch (error) {
    console.error('Failed to inject message styles:', error);
  }
};

const isArgsProps = (content: any): content is ArgsProps => {
  return typeof content === 'object' && content !== null && 'content' in content;
};

const wrapConfig = (
  content: ReactNode | ArgsProps,
  duration?: number,
  onClose?: () => void
): ArgsProps => {
  injectStyles();

  if (isArgsProps(content)) {
    return {
      ...content,
      className: `${MESSAGE_WRAPPER_CLASS} ${content.className || ''}`.trim(),
    };
  }

  return {
    content,
    duration,
    onClose,
    className: MESSAGE_WRAPPER_CLASS,
  } as ArgsProps;
};

export const message: MessageApi = {
  success: (content, duration, onClose) => antdMessage.success(wrapConfig(content, duration, onClose)),
  error: (content, duration, onClose) => antdMessage.error(wrapConfig(content, duration, onClose)),
  info: (content, duration, onClose) => antdMessage.info(wrapConfig(content, duration, onClose)),
  warning: (content, duration, onClose) => antdMessage.warning(wrapConfig(content, duration, onClose)),
  warn: (content, duration, onClose) => antdMessage.warn(wrapConfig(content, duration, onClose)),
  loading: (content, duration, onClose) => antdMessage.loading(wrapConfig(content, duration, onClose)),
  open: (config) => antdMessage.open(wrapConfig(config)),
  config: (options: ConfigOptions) => antdMessage.config(options),
  destroy: (key) => antdMessage.destroy(key),
  useMessage: antdMessage.useMessage,
};

export default message;
